"use client";

import { signOut } from "aws-amplify/auth";
import { useCallback, useEffect, useRef, useState } from "react";
import { apiUrl } from "@/lib/aicheck/api-base";
import { authService } from "@/services/auth";
import { useAuthStore } from "@/stores/auth";

const ACTIVITY_EVENTS = ["keydown", "click"] as const;

const THROTTLE_MS = 30_000; // Track activity once per 30s
const WARNING_BEFORE_SECONDS = 300; // Show warning 5 minutes before timeout

async function idleSignOut() {
  // Do NOT set manualSignOut flag — let layout's redirect logic
  // preserve returnUrl when it detects unauthenticated state
  sessionStorage.removeItem("manualSignOut");
  await signOut();
  useAuthStore.getState().clearState();
}

/**
 * @param enabled - Pass `true` only when authentication is confirmed.
 *   This prevents the hook from fetching the timeout config before the
 *   Amplify session is ready.
 */
export function useIdleLogout(enabled: boolean): {
  showWarning: boolean;
  secondsLeft: number;
  staySignedIn: () => void;
  signOutNow: () => void;
} {
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [timeoutMs, setTimeoutMs] = useState<number | null>(null);

  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastActivityRef = useRef(Date.now());
  const throttleRef = useRef(0);

  const clearAllTimers = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  const startTimers = useCallback(
    (timeout: number) => {
      clearAllTimers();
      lastActivityRef.current = Date.now();

      // For short timeouts, cap warning-before to 80% of timeout so
      // the warning always has a meaningful delay before appearing.
      const warningBeforeMs = Math.min(
        WARNING_BEFORE_SECONDS * 1000,
        Math.floor(timeout * 0.8),
      );
      const warningMs = Math.max(0, timeout - warningBeforeMs);

      // Warning timer
      warningTimerRef.current = setTimeout(() => {
        setShowWarning(true);
        const deadlineMs = lastActivityRef.current + timeout;
        setSecondsLeft(
          Math.max(0, Math.round((deadlineMs - Date.now()) / 1000)),
        );
        countdownRef.current = setInterval(() => {
          const remaining = Math.max(
            0,
            Math.round((deadlineMs - Date.now()) / 1000),
          );
          setSecondsLeft(remaining);
          if (remaining <= 0 && countdownRef.current) {
            clearInterval(countdownRef.current);
            countdownRef.current = null;
          }
        }, 1000);
      }, warningMs);

      // Logout timer
      idleTimerRef.current = setTimeout(() => {
        clearAllTimers();
        setShowWarning(false);
        idleSignOut();
      }, timeout);
    },
    [clearAllTimers],
  );

  const resetTimers = useCallback(() => {
    if (timeoutMs) {
      setShowWarning(false);
      setSecondsLeft(0);
      startTimers(timeoutMs);
    }
  }, [timeoutMs, startTimers]);

  // Fetch timeout config — only when enabled (auth is confirmed)
  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;

    async function fetchTimeout() {
      try {
        const token = await authService.getIdToken();
        if (!token || cancelled) return;

        const res = await fetch(apiUrl("/api/config/idle-timeout"), {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (cancelled) return;

        if (res.status === 401) {
          // Server-side idle timeout triggered — sign out immediately
          await idleSignOut();
          return;
        }

        if (!res.ok) return;

        const data = (await res.json()) as { idleTimeoutMinutes: number };
        if (!cancelled) {
          setTimeoutMs(data.idleTimeoutMinutes * 60 * 1000);
        }
      } catch {
        // Network error — do not sign out, just skip idle tracking
      }
    }

    fetchTimeout();
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  // Start timers when timeoutMs is set and enabled
  useEffect(() => {
    if (timeoutMs && enabled) {
      startTimers(timeoutMs);
    }
    return clearAllTimers;
  }, [timeoutMs, enabled, startTimers, clearAllTimers]);

  // Activity event listeners
  useEffect(() => {
    if (!timeoutMs || !enabled) return;

    const onActivity = () => {
      // Once the warning dialog is visible, ignore activity events.
      // Only the explicit "Stay Signed In" button can reset the timers.
      if (showWarning) return;

      const now = Date.now();
      if (now - throttleRef.current < THROTTLE_MS) return;
      throttleRef.current = now;
      resetTimers();
    };

    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, onActivity, { passive: true });
    }

    return () => {
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, onActivity);
      }
    };
  }, [timeoutMs, enabled, showWarning, resetTimers]);

  const staySignedIn = useCallback(() => {
    resetTimers();
  }, [resetTimers]);

  const signOutNow = useCallback(() => {
    clearAllTimers();
    setShowWarning(false);
    authService.signOut();
  }, [clearAllTimers]);

  return { showWarning, secondsLeft, staySignedIn, signOutNow };
}
